/** Activity: what was done, to which record, by whom, in plain words (B11) - a table page
 * (ADR-0212, P10).
 *
 * A filtered reading of the same record the Audit ledger shows whole, for the question *"what
 * happened to this?"* rather than *"does the record add up?"*. The two are kept apart on purpose.
 *
 * **It is a list, never a check.** A list with entries left out by a filter cannot be verified, so
 * nothing on this screen carries a tick, a hash or a verdict, and the server's own sentence saying
 * so (`not_a_check`) is one control away (the "A list, not a check" link opens it). The Audit ledger
 * is where the record is read whole and checked; each row's number opens its entry there.
 *
 * **Every sentence is the server's.** It is built from declared payload fields only
 * (`ledger.activity`), so this screen holds no wording for any act and cannot describe one
 * differently from the next screen (design rule 7). The Act column prints it verbatim.
 *
 * **A person is an identifier here, as everywhere in the record** (rule 4). The row names its actor
 * by kind and a short id, and "only this person" sets the filter; who an id belongs to is answered
 * under People and roles, against a table an erasure request can reach.
 *
 * **Who reads it, and the refusal.** The roles that read the chain whole (`MAY_READ`, mirrored from
 * `api/ledger.py`); anyone else is told who does, and an Auditor whose engagement covers a period or
 * particular companies is told why a list of entries cannot be narrowed to it (ADR-0048 item 5,
 * ADR-0113). A refusal is its own sentence, not a failed read.
 */

import { ClockCounterClockwise } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { NO_FILTERS, PAGE_SIZE, isUsableId, useActivity } from "../api/activity";
import type { ActivityFilters, ActivityRow } from "../api/activity";
import { useMe } from "../api/auth";
import { useReader } from "../api/profile";
import { A_COLLEAGUE, PersonName } from "./PersonRef";
import { Refused } from "../api/refusal";
import { ReadFailed } from "../components/Loaded";
import {
  Drawer,
  EMPTY_FILTERS,
  EmptyState,
  FilterLine,
  GridTable,
  InfoPopover,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import { actorKindWords, customerText, speakCodes, systemActorWords } from "../lib/customerWords";
import { instantDate } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import own from "./Activity.module.css";
import { WhyNote } from "./auditKit";
import { useGridSort } from "./gridSort";
import { atTime } from "./ledgerWords";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import styles from "./Screen.module.css";
import { useScreen } from "./useScreen";

/** Mirrored from `api/ledger.py`'s `MAY_READ`. A list rather than a negation, so a sixth role added
 * to the product defaults to being told who can rather than to seeing the record. */
const MAY_READ: readonly string[] = ["admin", "approver", "auditor"];

const ENGAGED_WORDS: Record<"period" | "companies", string> = {
  period:
    "An entry records when something was done, not which reporting period it was about, so a list of " +
    "entries cannot be narrowed to your engagement, and a list with entries left out could not be " +
    "verified. Every figure you are engaged for still resolves to its own entry through its receipt.",
  companies:
    "An entry records that something was done, not which company it was done for, so a list of " +
    "entries cannot be narrowed to the companies you are engaged for. Every figure you are engaged " +
    "for still resolves to its own entry through its receipt.",
};

/** The date and the time of an act, in the reader's own preferences; its own component so the
 * preferences are read only when there is an act to place in time. */
function When({ at }: { at: string }) {
  const reader = useReader();
  const time = atTime(at, reader);
  return (
    <span>
      {instantDate(at, reader)}
      {time ? ` ${time}` : ""}
    </span>
  );
}

function ActivityBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const [filters, setFilters] = useState<ActivityFilters>(NO_FILTERS);
  const [line, setLine] = useState<FilterState>(EMPTY_FILTERS);
  const [before, setBefore] = useState<number | undefined>(undefined);
  const [actorText, setActorText] = useState("");
  const [recordText, setRecordText] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);

  const mayRead = me ? MAY_READ.includes(me.role) : undefined;
  const narrowedBy = me?.engagement ?? null;
  const page = useActivity(filters, before, mayRead === true && narrowedBy === null);

  const refusal = page.error instanceof Refused ? page.error : null;
  const engagement =
    refusal?.code === "chain_has_no_period" || refusal?.code === "chain_has_no_entity" ? refusal : null;
  const narrowing: "period" | "companies" | null =
    narrowedBy ??
    (engagement === null ? null : engagement.code === "chain_has_no_entity" ? "companies" : "period");

  const apply = (next: Partial<ActivityFilters>) => {
    setFilters((current) => ({ ...current, ...next }));
    setBefore(undefined);
  };
  const filtered = filters.types.length > 0 || filters.actorId !== "" || filters.recordId !== "";
  const actorBad = !isUsableId(actorText);
  const recordBad = !isUsableId(recordText);

  const data = page.data;
  const held = data?.rows ?? [];
  const needle = line.search.trim().toLowerCase();
  const rows = held.filter((row) => !needle || row.sentence.toLowerCase().includes(needle));
  const { sorted, sort, setSort } = useGridSort(rows, {
    seq: (r) => r.seq,
    when: (r) => r.recorded_at,
    act: (r) => r.sentence,
  });
  const newest = held[0];

  const columns: GridColumn<ActivityRow>[] = [
    {
      id: "act",
      header: "Act",
      sortable: true,
      rowHeader: true,
      // The sentence is the act: it wraps and is not cut to "A narrative statement was written for a...".
      wrap: true,
      cell: (row) => <span data-testid="activity-sentence">{speakCodes(customerText(row.sentence))}</span>,
    },
    {
      id: "seq",
      header: "Entry",
      sortable: true,
      cell: (row) => (
        <Link
          className={`num ${own.seqLink}`}
          to="/ledger/$seq"
          params={{ seq: String(row.seq) }}
          search={keepSearch}
          aria-label={`Open entry ${String(row.seq)} on the audit ledger`}
          data-testid="activity-entry-open"
        >
          #{row.seq}
        </Link>
      ),
    },
    { id: "when", header: "When", sortable: true, cell: (row) => <When at={row.recorded_at} /> },
    {
      id: "actor",
      header: "By",
      cell: (row) => (
        <span data-testid="activity-actor">
          {row.actor_id ? (
            <button
              type="button"
              className={styles.fixLink}
              onClick={() => {
                setActorText(row.actor_id as string);
                apply({ actorId: row.actor_id as string });
              }}
              title={row.actor_id}
              data-testid="activity-only-person"
            >
              {actorKindWords(row.actor_kind)} <PersonName userId={row.actor_id} fallback={A_COLLEAGUE} />: only this person
            </button>
          ) : (
            // Null is the engine, the extractor or the scheduler: a different fact from "we did
            // not record who", which is why the kind travels with it.
            <span title={row.actor_kind}>{systemActorWords(row.actor_kind)}</span>
          )}
        </span>
      ),
    },
    {
      id: "more",
      header: "More",
      cell: (row) => (
        <span className={own.rowButtons}>
          {row.record_id ? (
            <button
              type="button"
              className={styles.fixLink}
              onClick={() => {
                setRecordText(row.record_id as string);
                apply({ recordId: row.record_id as string });
              }}
              data-testid="activity-only-record"
            >
              Everything about this record
            </button>
          ) : null}
          {row.calculation_id ? (
            <button
              type="button"
              className={styles.fixLink}
              onClick={() => screen.openReceipt(row.calculation_id as string)}
              data-testid="activity-receipt"
            >
              Open this figure&rsquo;s receipt
            </button>
          ) : null}
        </span>
      ),
    },
  ];

  if (mayRead === false || narrowing) {
    return (
      <ScreenFrame screen={screen} title="Activity" crumb="what was done, to which record, and by whom" bodyScrolls>
        <TablePage
          icon={ClockCounterClockwise}
          title="Approvals & audit"
          label="Activity"
          actions={<InfoPopover infoKey="audit.activity" />}
          state={
            mayRead === false ? (
              <WhyNote
                testId="activity-who-can"
                lead="An Admin, an Approver or an Auditor reads the activity."
                whyTitle="Why you cannot read the activity"
                why="It is a view of the record the audit ledger holds, which is an assurance view of the workspace rather than a figure. Every figure you can see still opens a receipt showing its own entry."
              />
            ) : narrowing ? (
              <WhyNote
                testId="activity-no-period"
                asked={engagement ? "refused" : "not-asked"}
                lead={
                  narrowing === "companies"
                    ? "Your engagement covers particular companies, and a list of entries cannot be narrowed to them."
                    : "Your engagement covers a period, and a list of entries cannot be narrowed to it."
                }
                whyTitle="Why the activity is not shown to you"
                why={engagement ? engagement.message : ENGAGED_WORDS[narrowing]}
              />
            ) : undefined
          }
        />
      </ScreenFrame>
    );
  }

  return (
    <ScreenFrame screen={screen} title="Activity" crumb="what was done, to which record, and by whom" bodyScrolls>
      <TablePage
        icon={ClockCounterClockwise}
        title="Approvals & audit"
        label="Activity"
        actions={<InfoPopover infoKey="audit.activity" />}
        tiles={
          data ? (
            <TileStrip label="The activity at a glance">
              <Tile
                value={`${String(held.length)}${data.next_before_seq === null ? "" : "+"}`}
                label="Acts on this page"
                testId="activity-listed"
              />
              <Tile value={newest ? `#${String(newest.seq)}` : "None"} label="Newest act" testId="activity-newest-act" />
              {/* The catalogue of kinds the product can record is not what happened here: with nothing
                  recorded a count of 91 reads as a result. */}
              <Tile
                value={held.length === 0 ? "-" : String(data.type_options.length)}
                label="Kinds of act"
                testId="activity-kinds"
              />
            </TileStrip>
          ) : undefined
        }
        filters={
          page.error || (data && held.length === 0 && !filtered) ? undefined : (
            <FilterLine
              filters={[
                {
                  id: "type",
                  label: "Kind of act",
                  mode: "single",
                  allLabel: "Any",
                  options: (data?.type_options ?? []).map((option) => ({
                    value: option.type,
                    label: option.label,
                  })),
                },
              ]}
              state={{ search: line.search, values: { type: filters.types } }}
              onChange={(next) => {
                setLine({ ...line, search: next.search });
                const chosen = next.values.type ?? [];
                if (chosen.join() !== filters.types.join()) apply({ types: [...chosen] });
              }}
              resultCount={data ? { shown: rows.length, total: held.length, noun: "acts on this page" } : undefined}
              actions={
                <form
                  className={own.idForm}
                  data-testid="activity-filters"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (!actorBad && !recordBad) {
                      apply({ actorId: actorText.trim(), recordId: recordText.trim() });
                    }
                  }}
                >
                  <input
                    type="text"
                    aria-label="Person (their id, not their name)"
                    placeholder="Person id"
                    value={actorText}
                    onChange={(event) => setActorText(event.target.value)}
                    aria-invalid={actorBad}
                    autoComplete="off"
                    data-testid="activity-actor-input"
                  />
                  <input
                    type="text"
                    aria-label="Record (its id)"
                    placeholder="Record id"
                    value={recordText}
                    onChange={(event) => setRecordText(event.target.value)}
                    aria-invalid={recordBad}
                    autoComplete="off"
                    data-testid="activity-record-input"
                  />
                  <button
                    type="submit"
                    className={styles.secondary}
                    disabled={actorBad || recordBad}
                    data-testid="activity-apply"
                  >
                    Apply
                  </button>
                  {filtered ? (
                    <button
                      type="button"
                      className={styles.fixLink}
                      data-testid="activity-clear"
                      onClick={() => {
                        setFilters(NO_FILTERS);
                        setLine(EMPTY_FILTERS);
                        setActorText("");
                        setRecordText("");
                        setBefore(undefined);
                      }}
                    >
                      Clear filters
                    </button>
                  ) : null}
                  {actorBad || recordBad ? (
                    <span className={own.hint} role="status" data-testid="activity-id-hint">
                      An id looks like 8-4-4-4-12 letters and digits. Use &ldquo;only this person&rdquo; on a
                      row to fill it in.
                    </span>
                  ) : null}
                  {data ? (
                    <button type="button" className={styles.fixLink} onClick={() => setNoteOpen(true)}>
                      A list, not a check
                    </button>
                  ) : null}
                </form>
              }
            />
          )
        }
        table={
          page.error ? undefined : (
            <>
              <GridTable
                caption={filtered ? "Recorded acts matching your filters, newest first" : "Recorded acts, newest first"}
                columns={columns}
                rows={sorted}
                rowKey={(row) => String(row.seq)}
                rowAttributes={(row) => ({ "data-testid": "activity-row", "data-type": row.type })}
                loading={page.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={
                  <EmptyState
                    compact
                    testId="activity-empty"
                    icon={held.length === 0 && !filtered ? ClockCounterClockwise : undefined}
                    title={
                      held.length > 0
                        ? "No act on this page matches the search."
                        : filtered
                          ? "Nothing recorded matches these filters."
                          : "No activity yet"
                    }
                  >
                    {held.length === 0 && !filtered ? "What people do in this workspace is recorded here." : null}
                  </EmptyState>
                }
                testId="activity-list"
              />
              {data && (held.length > 0 || filtered || before !== undefined) ? (
                <div className={own.pager}>
                  {data.next_before_seq === null ? (
                    <StatusChip tone="neutral" size="sm" testId="activity-end">
                      That is every matching act
                    </StatusChip>
                  ) : (
                    <button
                      type="button"
                      className={styles.fixLink}
                      data-testid="activity-older"
                      onClick={() => setBefore(data.next_before_seq as number)}
                    >
                      Older acts (next {PAGE_SIZE})
                    </button>
                  )}
                  {before === undefined ? null : (
                    <button
                      type="button"
                      className={styles.fixLink}
                      data-testid="activity-newest"
                      onClick={() => setBefore(undefined)}
                    >
                      Back to the newest
                    </button>
                  )}
                </div>
              ) : null}
            </>
          )
        }
        state={
          page.error ? (
            <ReadFailed of="The activity" error={page.error} onRetry={() => void page.refetch()} />
          ) : undefined
        }
      />

      {/* The server's own sentence about what this list is not: printed verbatim, one control away. */}
      <Drawer open={noteOpen} onClose={() => setNoteOpen(false)} title="A list, not a check" width="sm">
        {data ? <p data-testid="activity-not-a-check">{data.not_a_check}</p> : null}
      </Drawer>
    </ScreenFrame>
  );
}

export function ActivityScreen() {
  return (
    <RequireSession>
      <ActivityBody />
    </RequireSession>
  );
}
